// SEOintent changelog — single source of truth for /changelog page + dashboard "What's New" card.
//
// HOW TO ADD AN ENTRY (Claude or Leo):
//   Drop a new object at the TOP of the array (newest first). Keep it short — bullet-point style.
//   Required fields:
//     date     — ISO date "YYYY-MM-DD"
//     title    — 1 short line, the headline
//     category — "New" | "Improved" | "Fixed"
//     what     — one line: what we shipped (plain English, no jargon)
//     benefit  — one line: how it helps the customer
//
// Rule: PRODUCT changes that customers see in the app. NOT SEOintent's own marketing
// work (blog posts, pSEO pages, syndication runs, backlink submissions, directory
// listings, on-site SEO fixes, press mentions). Those live in the admin-only
// Marketing Operations feed at /admin/pseo (visible to leo@socialorange.in +
// admin@seointent.com only). See CLAUDE.md for the full rule.

const CHANGELOG = [
  {
    date: "2026-09-25",
    title: "Chrome extension welcome page",
    category: "Fixed",
    what: "Installing the Chrome extension now opens a proper welcome page with setup steps, instead of a 404.",
    benefit: "New users can pin the extension and run their first check in about a minute.",
  },
  {
    date: "2026-08-04",
    title: "Customer-facing Automated pSEO & Page Planner",
    category: "New",
    what: "Pulls real product catalogs from Shopify and WooCommerce stores, builds a custom pSEO page plan, and auto-writes 100 or 200 page packs complete with product cards, schema markup, and AI images.",
    benefit: "Scale your store's search traffic automatically. Preview 100+ proposed page ideas at /pseo, pick your pack, and publish straight to your store in your own brand voice.",
  },
  {
    date: "2026-05-27",
    title: "Affiliate earnings calculator + competitor comparison pages",
    category: "New",
    what: "Added /affiliate-program/earnings-calculator — interactive sliders for referrals/month, plan mix, and retention; computes year-1 income, recurring monthly at month 12, and 3-year LTV per referral. Also added /clearscope-alternative, /marketmuse-alternative, /vs/clearscope-vs-semrush, and /vs/marketmuse-vs-clearscope as comparison pages. Hub pages now show the full /use-cases/* spoke index at the bottom for better discovery.",
    benefit: "Affiliates can now show prospects exactly what they could earn. Buyers comparing competitor tools land on honest side-by-sides instead of generic marketing. Hub pages link to every relevant use-case so Google can find them.",
  },
  {
    date: "2026-05-27",
    title: "Affiliate program — dashboard is now reachable + persona pages",
    category: "Fixed",
    what: "Fixed the prod build error that was blocking the affiliate dashboard from loading (Rules of Hooks violation in AffiliateDashboardPage). Also added three persona-specific landing pages: /affiliate-program/for/seo-agencies, /affiliate-program/for/content-creators, and /affiliate-program/for/india (UPI payouts explained).",
    benefit: "If you applied as an affiliate, /affiliate-dashboard now loads your stats, referral link, and payout history. Persona pages give you direct links to share with the audience that matches them.",
  },
  {
    date: "2026-05-26",
    title: "Affiliate program with dashboard and coupon codes",
    category: "New",
    what: "Full referral program at /affiliates — 30% recurring for life, 90-day cookie, real-time dashboard with funnel + earnings + payouts, your own coupon code (e.g. LEO30 = 30% off first payment), and payouts via PayPal, Wise, UPI, or bank transfer.",
    benefit: "Earn recurring income by recommending SEOintent. No spreadsheets — your dashboard shows clicks, signups, and pending payouts the moment they happen.",
  },
  {
    date: "2026-05-21",
    title: "Auto-syndication to Dev.to, LinkedIn, and WordPress.com",
    category: "New",
    what: "Every new blog post on seointent.com now publishes to Dev.to, LinkedIn, and WordPress.com automatically — with a rel=canonical link pointing back so Google still credits the original.",
    benefit: "Three new backlinks and a referral-traffic bump for every article, with zero extra work. Dev.to is live today; LinkedIn and WordPress.com flip on after a one-time browser sign-in.",
  },
  {
    date: "2026-05-14",
    title: "Public changelog page",
    category: "New",
    what: "Launched /changelog — a single timeline of every customer-facing update we ship.",
    benefit: "You can see what's new at a glance and know the product keeps getting better, with no email digest needed.",
  },
  {
    date: "2026-05-11",
    title: "Backlink tracker dashboard",
    category: "New",
    what: "Master tracker for every backlink target with status, DR, and submission notes in one view.",
    benefit: "Stop juggling spreadsheets — see at a glance which sites are accepting your listing and which are stuck.",
  },
  {
    date: "2026-05-11",
    title: "Self-serve Search Console + GA4 inside the app",
    category: "New",
    what: "Admin SEO endpoints pull GSC queries and GA4 traffic-by-source on demand.",
    benefit: "No more flipping between dashboards — your AI assistant can read your real search and traffic data when answering questions.",
  },
  {
    date: "2026-05-07",
    title: "All leads flow into Twenty CRM",
    category: "Improved",
    what: "Backfilled 32 historical contacts to Twenty + every new signup, demo, and chat capture now syncs automatically.",
    benefit: "Every prospect lives in one CRM — no lost leads stuck inside form tools.",
  },
  {
    date: "2026-05-06",
    title: "Cleaner generated articles",
    category: "Improved",
    what: "Cleanup pipeline strips 'Problem / Insight / Solution' scaffold labels, collapses doubled drafts, and skips Pexels photos from the same shoot.",
    benefit: "Published posts read like a human wrote them — no template residue, no near-identical hero images across your blog.",
  },
  {
    date: "2026-05-04",
    title: "AI lead summary + score on every contact",
    category: "New",
    what: "Phase 2 of the CRM pipeline writes a one-paragraph AI summary and a lead score onto every Twenty Person.",
    benefit: "Open a contact and see who they are, what they want, and how hot the lead is — without doing any research yourself.",
  },
  {
    date: "2026-05-04",
    title: "Daily blog ideas from Search Console",
    category: "New",
    what: "Job runs at 6am IST, pulls real GSC queries, asks Claude to cluster them, and drops 3–4 fresh blog ideas into your Airtable queue.",
    benefit: "You wake up to a fresh content backlog grounded in what people are actually searching for around your brand.",
  },
  {
    date: "2026-05-04",
    title: "Instant lead notifications",
    category: "New",
    what: "Every contact-form submission fans out to Slack, email, and a Google Sheet within seconds.",
    benefit: "You hear about hot leads while they're still on your site, instead of 8 hours later when you check email.",
  },
  {
    date: "2026-05-04",
    title: "Hunter.io enrichment on lead capture",
    category: "New",
    what: "Anonymous tool visitors get their URL captured and the domain auto-enriched via Hunter.io.",
    benefit: "Lead lists arrive pre-enriched — emails, job titles, company names already filled in.",
  },
  {
    date: "2026-05-03",
    title: "Smarter keyword clustering",
    category: "Improved",
    what: "Orphan keywords tagged separately, plus per-keyword badges for CSV-uploaded vs AI-filled fields.",
    benefit: "You always know which keywords you provided and which the AI added — full transparency over your cluster.",
  },
  {
    date: "2026-05-03",
    title: "Ready-to-publish gate on articles",
    category: "New",
    what: "Articles must be explicitly marked ready before /cms/publish will push them to WordPress or Shopify.",
    benefit: "No more accidental draft pushes — your live site only gets the posts you've signed off on.",
  },
  {
    date: "2026-05-02",
    title: "Keyword Intel: pagination, filters, bulk delete",
    category: "Improved",
    what: "The keyword table now paginates, filters by status, and supports bulk delete.",
    benefit: "Managing 2,000 tracked keywords feels as fast as managing 20.",
  },
  {
    date: "2026-05-01",
    title: "Content publishing SOP",
    category: "Improved",
    what: "Every published article auto-adds table of contents, FAQ schema, internal links, hero image, and CTA card.",
    benefit: "Posts ship SEO-ready by default — no manual cleanup before they go live.",
  },
  {
    date: "2026-05-01",
    title: "Brand crawler now supports WooCommerce",
    category: "Improved",
    what: "Brand intel auto-crawler handles WordPress + WooCommerce permalink structures.",
    benefit: "E-commerce brands get full product, category, and policy-page coverage on the first crawl.",
  },
];

export default CHANGELOG;

// Returns the N most recent entries (newest first).
export function getRecent(limit = 3) {
  return CHANGELOG.slice(0, limit);
}
